import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl-next';
import { Link } from 'react-resource-router';

import Button from '@atlaskit/button/standard-button';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import LabelIcon from '@atlaskit/icon/glyph/label';
import Tooltip from '@atlaskit/tooltip';

import { Expander } from 'src/components/sidebar';
import { Commit } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { useTagHooks } from '../hooks/tag-hooks';

import messages from './commit-page.i18n';
import {
  CreateTagWrapper,
  TagListWrapper,
  TagOverflowWarning,
  CommitCardWrapper,
} from './commit-page.styled';
import { RenderAddModal } from './commit-tags-modal';

const useTagCountLabel = (commit: Commit) => {
  const { tags } = useTagHooks(commit);
  const tagCount = tags.refs.length;
  const tagCountLabel = tags.hasMoreRefs ? '25+' : tagCount.toString();

  let message;
  if (tagCount === 1) message = messages.oneTag;
  else message = messages.tags;

  return { message, tagCountLabel };
};

export const ExpandTagCard = ({ commit }: { commit: Commit }): JSX.Element => {
  const intl = useIntl();

  const [addModal, setAddModal] = useState<boolean>(false);
  const { tags, createTag } = useTagHooks(commit);

  const tagLinks = tags.refs.map((tag, i) => [
    i > 0 && ', ',
    <Link
      key={i}
      href={`tag/${tag.name}`}
      onClick={() =>
        publishUiEvent({
          action: 'clicked',
          actionSubject: 'link',
          actionSubjectId: 'tagLink',
          source: 'commitPage',
        })
      }
    >
      {tag.name}
    </Link>,
  ]);

  const tagCount = tags.refs.length;

  const { message, tagCountLabel } = useTagCountLabel(commit);
  const label = (
    <FormattedMessage
      {...message}
      values={{ count: <strong>{tagCountLabel}</strong> }}
    />
  );

  return (
    <Expander
      icon={<LabelIcon label={intl.formatMessage(messages.tagIcon)} />}
      label={label}
    >
      <div>
        {RenderAddModal(createTag, addModal, setAddModal)}
        {tagCount > 0 && (
          <TagListWrapper>
            <CommitCardWrapper>{tagLinks}</CommitCardWrapper>
          </TagListWrapper>
        )}
        {tags.hasMoreRefs && (
          <TagOverflowWarning>
            <EditorInfoIcon
              label={intl.formatMessage(messages.tagOverflowWarning)}
            />
            {intl.formatMessage(messages.tagOverflowWarning)}
          </TagOverflowWarning>
        )}
        <CreateTagWrapper>
          <Button
            appearance="subtle-link"
            onClick={() => {
              publishUiEvent({
                action: 'clicked',
                actionSubject: 'button',
                actionSubjectId: 'addTagButton',
                source: 'commitPage',
              });
              setAddModal(!addModal);
            }}
          >
            {intl.formatMessage(messages.createTag)}
          </Button>
        </CreateTagWrapper>
      </div>
    </Expander>
  );
};

export const CollapseTagCard = ({
  commit,
}: {
  commit: Commit;
}): JSX.Element => {
  const intl = useIntl();

  const { message, tagCountLabel } = useTagCountLabel(commit);
  const label = (
    <FormattedMessage {...message} values={{ count: tagCountLabel }} />
  );
  return (
    <Tooltip content={label} position="left">
      <Button
        label={intl.formatMessage(messages.tagLabel)}
        appearance="subtle"
        iconBefore={<LabelIcon label={intl.formatMessage(messages.tagIcon)} />}
      />
    </Tooltip>
  );
};
